import { authFetch } from "lib/api/authFetch";
import { BACKEND_URL } from "lib/utils/baseUrl";
import { normalizeApiMessage } from "lib/i18n/apiMessage";

function normalizeApiMessageWithFallbackKey(
  payload,
  fallbackMessage,
  fallbackKey,
) {
  const normalized = normalizeApiMessage(payload, fallbackMessage);
  return {
    ...normalized,
    messageKey: normalized.messageKey || fallbackKey || "",
  };
}

export async function postSaveFrameFftInfo({
  upload_id,
  clean_file_name,
  times,
  frames,
  volume,
  locale,
}) {
  if (!upload_id) {
    return {
      success: false,
      ...normalizeApiMessageWithFallbackKey(
        { message: "Invalid request." },
        "Invalid request.",
        "api.project.file.fft.save.validation.invalidRequest",
      ),
    };
  }

  if (!Array.isArray(frames) || frames.length < 1) {
    return {
      success: false,
      ...normalizeApiMessageWithFallbackKey(
        { message: "No frame payload to save." },
        "No frame payload to save.",
        "api.project.file.fft.save.validation.emptyPayload",
      ),
    };
  }

  try {
    const fd = new FormData();
    const safeName = clean_file_name || upload_id;

    fd.append("upload_id", upload_id);
    fd.append("times", JSON.stringify(times ?? frames.map((f) => f.time)));
    fd.append("decibels", JSON.stringify(frames.map((f) => f.decibel)));
    fd.append("distances", JSON.stringify(frames.map((f) => f.distance)));
    fd.append("volume", JSON.stringify(volume));

    const dataUrlToFile = async (dataUrl, filename) => {
      const blob = await (await fetch(dataUrl)).blob();
      return new File([blob], filename, { type: "image/png" });
    };

    for (let i = 0; i < frames.length; i++) {
      const f = frames[i];
      const t = times?.[i] ?? f?.time ?? i;

      const frameFile = await dataUrlToFile(f.image, `${safeName}_frame_${t}.png`);
      const graphFile = await dataUrlToFile(f.graph, `${safeName}_graph_${t}.png`);
      const waveFile = await dataUrlToFile(
        f.waveform,
        `${safeName}_wav_${t}.png`,
      );

      fd.append("frame_images", frameFile);
      fd.append("graph_images", graphFile);
      fd.append("waveform_images", waveFile);
    }

    const url = new URL(`${BACKEND_URL}/project/${upload_id}/frame-fft/save`);
    if (locale) url.searchParams.set("locale", locale);

    const res = await authFetch(
      url.toString(),
      {
        method: "POST",
        body: fd,
      },
      {
        refreshUrlIncludes: "auth/refresh",
      },
    );

    const result = await res.json().catch(() => null);

    if (!result) {
      return {
        success: false,
        ...normalizeApiMessageWithFallbackKey(
          { message: "Failed to parse server response." },
          "Failed to parse server response.",
          "api.common.serverError",
        ),
      };
    }

    if (!res.ok || result?.success !== true) {
      const fallbackKey =
        res.status === 401
          ? "api.auth.token.expired"
          : res.status === 404
            ? "api.project.file.fft.save.notFound"
            : res.status === 400
              ? "api.project.file.fft.save.validation.invalidRequest"
              : "api.project.file.fft.save.failed";

      return {
        success: false,
        ...normalizeApiMessageWithFallbackKey(
          result,
          "Request failed.",
          fallbackKey,
        ),
        data: result.data ?? null,
      };
    }

    return {
      success: true,
      ...normalizeApiMessageWithFallbackKey(
        result,
        "Frame FFT info saved successfully.",
        "api.project.file.fft.save.success",
      ),
      data: result.data ?? null,
    };
  } catch (err) {
    console.error("postSaveFrameFftInfo request failed:", err);
    return {
      success: false,
      ...normalizeApiMessageWithFallbackKey(
        { message: "Server request failed." },
        "Server request failed.",
        "api.common.networkError",
      ),
      error: String(err),
    };
  }
}
